<template>
  <template v-if="loading">
    <div class="loading">
      <div
        class="d-flex flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      >
        <div class="max-w-md w-full space-y-8">
          <v-loader-animation
            :text="'text-black'"
            :loading-text-style="'text-black text-lg'"
            loading-text="loading"
          />
        </div>
      </div>
    </div>
  </template>
  <template v-else-if="firmware">
    <div class="form-container">
      <v-card
        class="mx-auto"
        :title="i18n.global.t('pages.firmwareList.form.title.update')"
      >
        <v-container>
          <v-checkbox
            v-model="firmware.active"
            :label="i18n.global.t('pages.firmwareList.valid')"
            color="secondary"
            hide-details
          ></v-checkbox>

          <div class="form-link">
            <label class="v-label v-field-label">
              {{ i18n.global.t('pages.firmwareList.form.label.firmware') }}
            </label>
            <div class="v-field__input">
              <v-btn v-if="issetFirmwareFile" :href="firmwareFilePath">
                {{ firmware.firmware?.file_name }}
              </v-btn>
              <template v-else> - </template>
            </div>
            <div class="v-input__details"><div class="v-messages"></div></div>
          </div>

          <v-text-field
            disabled
            v-model="firmware.checksum"
            color="primary"
            :label="i18n.global.t('pages.firmwareList.form.label.hash')"
            variant="underlined"
          />

          <MultipleDeviceTypeAutoComplete
            v-model="selectedDeviceTypes"
            @blur="validateDeviceTypes"
            :error-messages="errors.device_type"
          />

          <v-text-field
            class="mt-4"
            disabled
            v-model="firmware.version"
            color="primary"
            :label="i18n.global.t('pages.firmwareList.version')"
            variant="underlined"
          />

          <v-textarea
            :label="i18n.global.t('pages.firmwareList.form.label.memo')"
            v-model="firmware.memo"
            :hide-details="true"
            @change="validateMemo"
            :error-messages="errors.memo"
          />
        </v-container>
        <v-divider class="my-4"></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="ma-2 reset-btn" @click="$emit('close-modal')">
            {{ i18n.global.t('pages.common.button.close') }}
          </v-btn>
          <v-btn
            v-if="updateAuthority"
            class="ma-2 exec-btn"
            @click="validateAndSendForm"
            :style="{
              backgroundColor: updating ? '#B3B8BB' : '',
            }"
          >
            {{ updateBtnText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </template>
</template>
<script setup lang="ts">
import Firmware from '@/types/Firmware';
import MultipleDeviceTypeAutoComplete from '@/components/AutoCompletes/MultipleDeviceTypeAutoComplete.vue';
import vLoaderAnimation from '@/components/template/v-loader-animation.vue';
import { isDeviceTypesValide, isMemoValide } from '@/services/validators';
import { ref, watch, computed, ComputedRef } from 'vue';
import deviceType from '@/types/DeviceType';
import i18n from '@/lang/i18n';

const props = defineProps<{
  loading: boolean;
  updating: boolean;
  loadedFirmware: Firmware | undefined;
  updateAuthority: boolean;
}>();

const emit = defineEmits(['close-modal', 'send-form']);

const firmware = ref<Firmware | undefined>(undefined);
const selectedDeviceTypes = ref<deviceType[]>([]);
const errors = ref<{ device_type: Array<string>; memo: Array<string> }>({
  device_type: [],
  memo: [],
});

const issetFirmwareFile: ComputedRef<boolean> = computed(() => {
  return (
    firmware.value?.firmware?.file_name !== undefined &&
    firmware.value?.firmware?.file_name !== '-' &&
    firmware.value?.firmware?.path_to_file !== undefined &&
    firmware.value?.firmware?.path_to_file !== '-'
  );
});

const firmwareFilePath: ComputedRef<string | undefined> = computed(() => {
  if (issetFirmwareFile.value) {
    return getRestAPIUrl.value + firmware.value?.firmware?.path_to_file;
  }
  return undefined;
});

const getRestAPIUrl: ComputedRef<string> = computed(() => {
  if (process.env.VUE_APP_REST_API_BASE_URL.slice(-1) !== '/') {
    return process.env.VUE_APP_REST_API_BASE_URL + '/';
  }
  return process.env.VUE_APP_REST_API_BASE_URL;
});

const isValide: ComputedRef<boolean> = computed(() => {
  return Object.values(errors.value).every((errors) => errors.length === 0);
});

const updateBtnText: ComputedRef<string> = computed(() => {
  return props.updating ?
    i18n.global.t('pages.firmwareList.form.button.saving') :
    i18n.global.t('common.saveBtn');
});

const validateAndSendForm = () => {
  if (props.updating) {
    return;
  }
  validateForm();
  if (isValide.value) {
    sendForm();
  }
};

watch(
  () => props.loadedFirmware,
  (newValue) => {
    firmware.value = newValue;
    if (newValue && newValue.device_types.length > 0) {
      selectedDeviceTypes.value = newValue.device_types;
    }
  },
  { deep: true }
);

const validateForm = () => {
  validateDeviceTypes();
  validateMemo();
};

const validateDeviceTypes = () => {
  const errorMessage = isDeviceTypesValide(selectedDeviceTypes.value);
  if (errorMessage === null) {
    errors.value.device_type = [];
    return;
  }
  errors.value.device_type = [errorMessage];
};

const validateMemo = () => {
  let errorMessage = isMemoValide(firmware.value?.memo);
  if (errorMessage === null) {
    errors.value.memo = [];
    return;
  }
  errors.value.memo = [errorMessage];
};

const sendForm = () => {
  emit('send-form', {
    firmware_id: firmware.value?.id,
    active: firmware.value?.active,
    memo: firmware.value?.memo,
    ar_device_type_ids: selectedDeviceTypes.value.map(
      (deviceType) => deviceType.id
    ),
  });
};
</script>
<style scoped>
.form-container {
  padding: 15px;
}

.form-link .v-field__input {
  margin-top: 0;
  padding-top: 0;
}

.form-link .v-label {
  position: initial;
  margin-bottom: -5px;
}
</style>
